import "./DiagnosisAutosuggest.css";
import "./DiagnosisDialog.css";

import {
  MouseEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useHotkeysContext } from "react-hotkeys-hook";
import { useDispatch, useSelector } from "react-redux";
import { useLocalStorage } from "react-use";

import {
  Icd10Code,
  MacroCode,
  SlideClassification,
  Specimen,
  User,
} from "pwapi";

import { CaseDiagnosis } from "custom-models";
import { CptInput } from "custom-models";

import { fetchSpecimenClassification } from "shared/actions/aiActions";
import { createCaseDiagnosis } from "shared/actions/caseDetailActions";
import { optimisticDiagnosisUpdate } from "shared/actions/specimenDetailActions";
import { useHasLoaded } from "shared/hooks/requestStatusHooks";
import { LesionLine, lesionLineTypeByName } from "shared/reducers/aiReducer";
import { CaseDetailState } from "shared/reducers/caseDetailReducer";
import { FeatureFlags } from "shared/reducers/sysReducer";

import Spinner from "components/Spinner";
import { useForcedModifier } from "hooks/useForcedModifier";
import { ViewerItem } from "hooks/useViewerItems";
import { selectAutoCptCodesEnabled } from "selectors/autoCptCodesEnabled";
import { selectSpecimenClassification } from "selectors/classificationSelector";
import ApplicationState from "store/ApplicationState";
import { useAppSelector } from "store/hooks";

import DiagnosisDialogAI from "./DiagnosisDialogAI";
import {
  validityOfDiagnosis,
  createApiCptList,
  reduceCptCodes,
} from "./DiagnosisDialogHelpers";
import AutoCPTCodes, {
  cptChargesForSpecimen,
} from "./dialog-inputs/AutoCPTCodes";
import CPTInputGroup from "./dialog-inputs/CPTInputGroup";
import ICD10Input from "./dialog-inputs/ICD10Input";
import ZSDInput from "./dialog-inputs/ZSDInput";
import MacroCodeInput, {
  extractHelpers,
  reconstructMacroCode,
} from "./macro_code/MacroCodeInput";
import Sidebar from "./Sidebar";
import SidebarFooter from "./SidebarFooter";
import SignOutButton from "./SignOutButton";
import {
  createListString,
  getHighestVersionDiagnosis,
  isDigitizationCode,
} from "./utils";
import PermanentlyResizableTextarea from "../PermanentlyResizableTextarea";

export type DiagnosisDialogProps = {
  caseDetailObject: CaseDetailState;
  specimen: Specimen;
  currentSlideId: number;
  user?: User;
  readOnly?: boolean;
  setDialog?: Function;
  aiObject?: SlideClassification;
  nextSpecimenViewerItem?: ViewerItem;
  onHandleNextSpecimen: MouseEventHandler<HTMLButtonElement>;
};
export interface ShadowDiagnosis extends Partial<MacroCode> {
  front?: string;
  margin?: string;
}
export type SiteSpecificCodeReplacement = {
  originalCode: Icd10Code;
  replacementCode: Icd10Code;
};

const emptyDiagnosis = {
  diagnosis: "",
  microscopic: "",
  macro_code: "",
  comment: "",
  zsd_code: "",
  icd10_code: "",
  cpt_codes: [],
};

const DiagnosisDialog = ({
  caseDetailObject,
  currentSlideId, // needed for Specimen
  user,
  setDialog,
  aiObject,
  specimen,
  nextSpecimenViewerItem,
  onHandleNextSpecimen,
}: DiagnosisDialogProps) => {
  const dispatch = useDispatch();
  const { maskLesionLineType } = useAppSelector((state) => state.ai);
  const { forcedModifier } = useForcedModifier();
  const macroCodeInput = useRef<HTMLInputElement>(null);

  const [commonWidth, setCommonWidth] = useLocalStorage<number | undefined>(
    "DIAGNOSIS_TEXTAREA_WIDTH"
  );

  // TODO: the below is clearly un-ergonomic
  const currentSpecimenDetail = caseDetailObject.specimenDetail.filter(
    (s) => s.id === specimen?.id
  )[0];
  const screenDiagnosis = getHighestVersionDiagnosis(
    currentSpecimenDetail?.diagnoses?.filter(
      (d) => d.source === CaseDiagnosis.source.SCREEN && d.is_completed
    )
  );
  const specimensDiagnosis = currentSpecimenDetail?.diagnoses?.filter(
    (d) => d.user && d.user.id === user?.id && !d.is_completed
  );

  const currentDiagnosis = specimensDiagnosis?.length
    ? specimensDiagnosis[0]
    : undefined;

  const [icd10Error, setIcd10Error] = useState<boolean>(false);
  const [zsdError, setZsdError] = useState<boolean>(false);
  const [cptError, setCptError] = useState<boolean>(false);
  const [loadingKeyPressed, setLoadingKeyPressed] = useState<string>("");
  useState<number>(0);

  const {
    diagnosis,
    microscopic,
    comment,
    zsd_code: zsd,
    cpt_codes,
    icd10_code: icd10,
  } = currentDiagnosis || {};

  // An 'enhanced' set of cpt_codes
  // We need to enhance the basic string[] to track key ids
  // Kicks off once and then is managed by the component
  // When macro codes execute, these naturally 'wipe' and are
  // ... replaced by new UUIDs for key tracking
  const [cpt, setCpt] = useState<CptInput[]>(
    // We only want to edit "natural" CPT codes
    reduceCptCodes(
      (cpt_codes || []).filter((cptCode) => !isDigitizationCode(cptCode))
    )
  );

  const [siteSpecificCode, setSiteSpecificCode] =
    useState<SiteSpecificCodeReplacement>();

  const submitButton = useRef<HTMLButtonElement>(null);
  const macroExecuted = useRef<boolean>(false);

  const [shadowDiagnosis, setShadowDiagnosis] = useState<ShadowDiagnosis>({
    diagnosis: "",
  });

  useEffect(() => {
    setShadowDiagnosis({ diagnosis: "" });
  }, [specimen?.id]);

  //
  useEffect(() => {
    if (!currentDiagnosis?.id && specimen?.id) {
      // If there is a screen diagnosis and the case is_screening, the user is in a rescreen procedure.
      // Otherwise, do not create a new diagnosis
      const newCaseDiagnosis = screenDiagnosis
        ? caseDetailObject.is_screening
          ? {
              case: caseDetailObject.id || 0,
              specimen: specimen.id,
              user: user?.id || 0,
              diagnosis: screenDiagnosis.diagnosis,
              comment: screenDiagnosis.comment,
              microscopic: screenDiagnosis.microscopic,
              cpt_codes: screenDiagnosis.cpt_codes,
              icd10_code: screenDiagnosis.icd10_code,
              zsd_code: screenDiagnosis.zsd_code,
              macro_code: screenDiagnosis.macro_code,
              source: CaseDiagnosis.source.SCREEN,
            }
          : undefined
        : {
            case: caseDetailObject.id || 0,
            specimen: specimen.id,
            user: user?.id || 0,
            diagnosis: "",
            cpt_codes: [],
            source: caseDetailObject.is_screening
              ? CaseDiagnosis.source.SCREEN
              : CaseDiagnosis.source.FINALIZE,
          };
      if (newCaseDiagnosis) {
        //@ts-ignore
        dispatch(createCaseDiagnosis(newCaseDiagnosis));
        screenDiagnosis &&
          caseDetailObject.is_screening &&
          setCpt(reduceCptCodes(screenDiagnosis.cpt_codes || []));
      }
    }
  }, [
    caseDetailObject.id,
    specimen?.id,
    currentDiagnosis?.id,
    screenDiagnosis,
    user?.id,
  ]);

  const { disableScope, enableScope } = useHotkeysContext();

  // Handle missing macro text if a user starts typing while the diagnosis is loading.
  // In this case, store the keystrokes in state and add them to the macro code when it loads.
  useEffect(() => {
    const storeKeyPress = (event: KeyboardEvent) => {
      if (/^[a-zA-Z0-9.:;]$/.test(event.key)) {
        setLoadingKeyPressed((prevKeys) => prevKeys + event.key);
      }
    };

    if (!currentDiagnosis) {
      enableScope("macroHotkeys");
      disableScope("viewerHotkeys");

      // Add event listener
      window.addEventListener("keydown", storeKeyPress);
    } else {
      setLoadingKeyPressed("");
      enableScope("viewerHotkeys");
      disableScope("macroHotkeys");
    }

    // Cleanup event listener on component unmount
    return () => {
      enableScope("viewerHotkeys");
      window.removeEventListener("keydown", storeKeyPress);
    };
  }, [currentDiagnosis]);

  const diagnosisWithFrontAndMargin = `${
    shadowDiagnosis.front ? `${shadowDiagnosis.front} ` : ""
  }${shadowDiagnosis.diagnosis ?? ""}${
    shadowDiagnosis.margin ? `, ${shadowDiagnosis.margin}` : ""
  }`;

  useEffect(() => {
    if (
      macroExecuted.current &&
      caseDetailObject.diagnosis_status === "success"
    ) {
      macroExecuted.current = false;
      diagnosisWithFrontAndMargin.length && submitButton.current?.focus();
    }
  }, [caseDetailObject.diagnosis_status, diagnosisWithFrontAndMargin]);

  const executeMacroCode = (
    executedCode?: string,
    macroDiagnosis?: ShadowDiagnosis
  ) => {
    macroExecuted.current = true;
    setIcd10Error(false);
    setZsdError(false);

    let newDiagnosis: Partial<CaseDiagnosis> = macroDiagnosis
      ? buildDiagnosisforApi(macroDiagnosis)
      : buildDiagnosisforApi(shadowDiagnosis);
    if (executedCode)
      newDiagnosis.macro_code = (({ front, margin, dxcode, comment }) => ({
        front,
        margin,
        dxcode,
        comment,
      }))(extractHelpers(executedCode, !!user?.enable_macro_helpers));
    updateDiagnosis(newDiagnosis);
  };
  const buildDiagnosisforApi = (macroDiagnosis: ShadowDiagnosis) => {
    const cptCode = forcedModifier
      ? {
          ...macroDiagnosis.cpt_code,
          code: macroDiagnosis.cpt_code?.code || "",
          modifier: forcedModifier,
        }
      : macroDiagnosis.cpt_code;

    const cptInputs = reduceCptCodes([cptCode || { code: "" }]);
    setCpt(cptInputs);

    return {
      diagnosis: `${macroDiagnosis.front ? `${macroDiagnosis.front} ` : ""}${
        macroDiagnosis.diagnosis ?? ""
      }${macroDiagnosis.margin ? `, ${macroDiagnosis.margin}` : ""}`,
      microscopic: macroDiagnosis?.microscopic_description || "",
      comment: macroDiagnosis?.note || "",
      zsd_code: macroDiagnosis?.zsd_code?.code || "",
      icd10_code: macroDiagnosis?.icd10_code?.code || "",
      cpt_codes: createApiCptList(cptInputs),
    };
  };

  const updateDiagnosis = useCallback(
    (diagnosis?: Partial<CaseDiagnosis>) => {
      if (specimen)
        dispatch(
          optimisticDiagnosisUpdate(
            // @ts-ignore
            { ...currentDiagnosis, ...diagnosis },
            specimen
          )
        );
    },
    [specimen, currentDiagnosis]
  );

  const clearDiagnosis = useCallback(() => {
    setCpt(reduceCptCodes([]));
    updateDiagnosis(emptyDiagnosis);
  }, [updateDiagnosis]);

  const features: FeatureFlags = useSelector(
    ({ sys }: ApplicationState) => sys.features
  );

  const specimenClassification = useAppSelector((state) =>
    selectSpecimenClassification(state, specimen?.id)
  );

  // TODO: DOES THIS WORK FOR SLIDE ONLY?
  // whats the difference between specimenClassification and aiObject. Why do we have aiObject -> that's just the slide
  // maybe we should call it slideClassification
  const positiveLesionLines = specimenClassification
    ?.flatMap((s) => s?.lesion_lines)
    .filter((s) => s?.diagnosis_result === "POSITIVE")
    .filter((v) => v) as LesionLine[];

  const lesionLine = (positiveLesionLines || []).find(
    (e: LesionLine) =>
      lesionLineTypeByName(e.diagnosis_name) === maskLesionLineType
  );

  const nonePositive =
    aiObject?.id &&
    aiObject?.lesion_lines?.length &&
    aiObject?.lesion_lines.every((l) => l.diagnosis_result !== "POSITIVE");

  const [prospectiveMacroCode, setProspectiveMacroCode] = useState("");

  // TODO: Verify the back end currently includes these in its /api/cases/{id}/ response
  const anyClassifications = caseDetailObject.slides.some(
    (slide) =>
      slide.classifications?.filter(
        (s) => s.status === SlideClassification.status.SUCCEEDED
      ).length
  );

  const mihm_assist = features?.prospectiveAI && anyClassifications;

  const prospectiveAI = !!(
    mihm_assist &&
    ((positiveLesionLines !== undefined && positiveLesionLines?.length > 0) ||
      nonePositive)
  );

  const checkErrors = (
    error?: boolean,
    errorType: "zsd" | "icd10" | "cpt" | "" = ""
  ) => {
    return (
      error ||
      (errorType === "zsd" ? error : zsdError) ||
      (errorType === "cpt" ? error : cptError) ||
      (errorType === "icd10" ? error : icd10Error)
    );
  };

  const specimenClassificationLoaded = useHasLoaded(
    fetchSpecimenClassification,
    { parentLookupCaseId: caseDetailObject.id, id: specimen?.id }
  ); // TODO: second argument should by generically typed
  const aiLoaded = specimenClassificationLoaded;

  const useAutoCptCodes = useAppSelector(selectAutoCptCodesEnabled);

  if (!currentDiagnosis)
    return (
      <Sidebar isWide>
        <div className="Sidebar__contentContainer">
          <Spinner
            size="24px"
            width="3px"
            frontColor="var(--teal)"
            backColor="var(--lightNoteGreen)"
          />
        </div>
      </Sidebar>
    );

  return (
    <Sidebar
      hasAutoWidth
      isScrollable
      hat={
        prospectiveAI &&
        aiLoaded && (
          <DiagnosisDialogAI
            {...{
              currentSpecimen: specimen,
              currentDiagnosis,
              prospectiveMacroCode,
              aiObject,
              positiveLesionLines,
              lesionLine,
              updateDiagnosis,
              setProspectiveMacroCode,
              macroCodeInputRef: macroCodeInput,
            }}
          />
        )
      }
    >
      <form className="Sidebar--assessment">
        <div className="DiagnosisDialog Sidebar__contentContainer">
          {prospectiveAI ? (
            <MacroCodeInput
              initialValue={prospectiveMacroCode || undefined}
              prospectiveAI={true}
              setShadowDiagnosis={setShadowDiagnosis}
              setDialog={setDialog}
              currentSpecimen={specimen}
              currentDiagnosis={currentDiagnosis}
              executeMacroCode={executeMacroCode}
              setSiteSpecificCode={setSiteSpecificCode}
              ref={macroCodeInput}
              clearDiagnosis={clearDiagnosis}
            />
          ) : (
            <MacroCodeInput
              initialValue={loadingKeyPressed}
              setShadowDiagnosis={setShadowDiagnosis}
              currentSpecimen={specimen}
              currentDiagnosis={currentDiagnosis}
              setDialog={setDialog}
              executeMacroCode={executeMacroCode}
              useHelpers={!!user?.enable_macro_helpers}
              setSiteSpecificCode={setSiteSpecificCode}
              ref={macroCodeInput}
              clearDiagnosis={clearDiagnosis}
            />
          )}

          <div className="DiagnosisDialog__diagnosis">
            <header className="DiagnosisDialog__header">
              <label>
                Diagnosis{" "}
                {currentDiagnosis.macro_code && (
                  <em>
                    (based on{" "}
                    <strong>
                      {reconstructMacroCode(currentDiagnosis.macro_code)}
                    </strong>
                    )
                  </em>
                )}
              </label>
            </header>
            <PermanentlyResizableTextarea
              id="diagnosis-textarea"
              rows={5}
              value={diagnosis || ""}
              onChange={(_e) => {
                updateDiagnosis({ diagnosis: _e.target.value });
              }}
              placeholder={diagnosisWithFrontAndMargin}
              commonWidth={commonWidth}
              setCommonWidth={setCommonWidth}
            />
          </div>
          <div className="DiagnosisDialog__findings">
            <header className="DiagnosisDialog__header">
              <label>
                Microscopic Findings
                {mihm_assist && (
                  <img
                    src="/pw-purple.png"
                    alt="Mihm Assist"
                    title="A Mihm Assist Disclaimer Will Be Added Upon Sign-Out"
                    height="16"
                    width="16"
                    style={{ marginBottom: "3px", marginLeft: "3px" }}
                  />
                )}
              </label>
            </header>
            <PermanentlyResizableTextarea
              id="microscopic-textarea"
              rows={5}
              value={microscopic || ""}
              onChange={(_e) => {
                updateDiagnosis({ microscopic: _e.target.value });
              }}
              placeholder={shadowDiagnosis.microscopic_description}
              commonWidth={commonWidth}
              setCommonWidth={setCommonWidth}
            />
          </div>
          <div className="DiagnosisDialog__comments">
            <header className="DiagnosisDialog__header">
              <label htmlFor="comments-textarea">Note</label>
            </header>
            <PermanentlyResizableTextarea
              id="comments-textarea"
              rows={5}
              value={comment || ""}
              onChange={(_e) => {
                updateDiagnosis({ comment: _e.target.value });
              }}
              placeholder={shadowDiagnosis.note || undefined}
              commonWidth={commonWidth}
              setCommonWidth={setCommonWidth}
            />
          </div>
          <div className="DiagnosisDialog__codes">
            <ICD10Input
              icd10={icd10 || ""}
              icd10Error={icd10Error}
              setIcd10={(newValue: string) =>
                updateDiagnosis({ icd10_code: newValue.toUpperCase() })
              }
              setIcd10Error={setIcd10Error}
              placeholder={shadowDiagnosis.icd10_code?.code}
              siteSpecificCode={siteSpecificCode}
            />
            <ZSDInput
              zsd={zsd || ""}
              zsdError={zsdError}
              setZsd={(newValue: string) =>
                updateDiagnosis({ zsd_code: newValue })
              }
              setZsdError={setZsdError}
              placeholder={shadowDiagnosis.zsd_code?.code}
            />
            {useAutoCptCodes ? (
              <AutoCPTCodes caseDetail={caseDetailObject} specimen={specimen} />
            ) : (
              <>
                <CPTInputGroup
                  cptCodes={cpt}
                  setCptCodes={(newValue: CptInput[]) => {
                    setCpt(newValue);
                    updateDiagnosis({
                      cpt_codes: createApiCptList(
                        newValue.filter((c) => !c.error)
                      ),
                    });
                  }}
                  setCptError={setCptError}
                  placeholder={shadowDiagnosis.cpt_code?.code}
                  glassRead={
                    specimen?.glass_read || caseDetailObject?.glass_read
                  }
                  currentDiagnosisCptCodes={cpt_codes}
                />
                {features?.previewAutoCPTCodes &&
                  caseDetailObject.charge_caches &&
                  cptChargesForSpecimen(caseDetailObject, specimen) && (
                    <AutoCPTCodes
                      caseDetail={caseDetailObject}
                      specimen={specimen}
                      preview={true}
                    />
                  )}
              </>
            )}
          </div>
        </div>

        <SidebarFooter>
          <div className="DiagnosisDialogActions">
            <span data-testid="error-span" className="form__error">
              {checkErrors(false) &&
                `Unrecognised ${createListString([
                  ...(icd10Error ? ["ICD10"] : []),
                  ...(zsdError ? ["ZSD"] : []),
                  ...(cpt.some((cpt) => cpt.error) ? ["CPT"] : []),
                ])} code`}
            </span>

            {!nextSpecimenViewerItem ? (
              <SignOutButton
                caseDetailObject={caseDetailObject}
                currentSlideId={currentSlideId}
                user={user}
                formErrors={checkErrors()}
                ref={submitButton}
                showSpinner
              />
            ) : (
              <button
                className="Button Button--lightBlue"
                type="button"
                disabled={
                  !validityOfDiagnosis(currentDiagnosis).valid ||
                  checkErrors() ||
                  caseDetailObject.diagnosis_status === "loading" ||
                  caseDetailObject.diagnosis_status === "error"
                }
                title={`Missing ${validityOfDiagnosis(
                  currentDiagnosis
                ).missing.join(", ")}`}
                onClick={onHandleNextSpecimen}
                ref={submitButton}
              >
                {caseDetailObject.diagnosis_status === "loading" && (
                  <Spinner
                    size={"16px"}
                    width={"3px"}
                    backColor={"var(--blue15)"}
                    frontColor={"var(--blue)"}
                  />
                )}
                Next Specimen&nbsp;&rarr;
              </button>
            )}
          </div>
        </SidebarFooter>
      </form>
    </Sidebar>
  );
};

export default DiagnosisDialog;
